import React, { useState, useEffect, useRef } from 'react';
import { X, MapPin, Navigation, Store, Phone, Globe, Clock, Star, Loader, List, Map, Search, Eye, EyeOff, Target, DollarSign, TrendingUp, TrendingDown, Minus, ExternalLink } from 'lucide-react';
import API_URL from '../config/ApiUrl';
import { useTheme } from '../arrangement/ThemeContext';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
console.log(GOOGLE_MAPS_API_KEY)
// EXACT SAME Google Maps loader as FindNearbyModal
const loadGoogleMapsAPI = () => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google?.maps?.places) {
      console.log('Google Maps API already loaded');
      resolve(window.google.maps);
      return;
    }

    // Check for existing promise
    if (window.googleMapsPromise) {
      window.googleMapsPromise.then(resolve).catch(reject);
      return;
    }

    window.googleMapsPromise = new Promise((resolvePromise, rejectPromise) => {
      // Remove any existing script
      const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
      if (existingScript && !window.google?.maps?.places) {
        existingScript.remove();
      }

      // Create new script with places library explicitly included
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      
      // Global callback function
      window.initGoogleMaps = () => {
        if (window.google?.maps?.places) {
          console.log('Google Maps API loaded successfully with Places');
          resolvePromise(window.google.maps);
        } else {
          console.error('Google Maps loaded but Places library missing');
          rejectPromise(new Error('Google Maps Places library not available'));
        }
        delete window.initGoogleMaps;
      };
      
      script.onerror = (error) => {
        console.error('Failed to load Google Maps script:', error);
        rejectPromise(new Error('Failed to load Google Maps API'));
        delete window.initGoogleMaps;
      };
      
      document.head.appendChild(script);
      
      // Timeout fallback
      setTimeout(() => {
        if (!window.google?.maps?.places) {
          rejectPromise(new Error('Google Maps API loading timeout'));
          delete window.initGoogleMaps;
        }
      }, 15000);
    });

    window.googleMapsPromise.then(resolve).catch(reject);
  });
};

export const ProductDispensariesModal = ({ isOpen, onClose, product, dispensaryIconUrl }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [showUserLocation, setShowUserLocation] = useState(true);
  const [isManualLocation, setIsManualLocation] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const { colors } = useTheme();
  
  const [dispensaries, setDispensaries] = useState([]);
  const [priceStats, setPriceStats] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isLoadingDispensaries, setIsLoadingDispensaries] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDispensary, setSelectedDispensary] = useState(null);
  const [searchRadius, setSearchRadius] = useState(25);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileView, setMobileView] = useState('list');
  const [sortBy, setSortBy] = useState('price');

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const userMarkerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectedMarkerRef = useRef(null);

  // Check for mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load Google Maps API - EXACT SAME AS WORKING VERSION
  useEffect(() => {
    let isMounted = true;
    
    loadGoogleMapsAPI()
      .then(() => {
        if (isMounted) {
          setIsMapLoaded(true);
        }
      })
      .catch((error) => {
        console.error('Error loading Google Maps:', error);
        if (isMounted) {
          setError('Failed to load map. Please refresh and try again.');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Initialize map when modal opens - ENHANCED WITH SMART CENTERING
  useEffect(() => {
    if (isOpen && isMapLoaded && mapRef.current) {
      const timer = setTimeout(() => {
        if (!mapInstanceRef.current) {
          try {
            initializeMap();
          } catch (error) {
            console.error('Error initializing map:', error);
            setError('Failed to initialize map. Please try again.');
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen, isMapLoaded, mobileView]);

  // Setup autocomplete - EXACT SAME AS WORKING VERSION  
  useEffect(() => {
    if (isMapLoaded && searchInputRef.current && isOpen && mapInstanceRef.current) {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }

      const timer = setTimeout(() => {
        try {
          if (!searchInputRef.current || !mapInstanceRef.current) return;

          const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
            types: ['establishment'],
            fields: ['place_id', 'geometry', 'name', 'formatted_address', 'address_components']
          });
          
          autocomplete.bindTo('bounds', mapInstanceRef.current);
          autocompleteRef.current = autocomplete;

          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (place.geometry) {
              const lat = place.geometry.location.lat();
              const lng = place.geometry.location.lng();
              
              setUserLocation({ lat, lng });
              setIsManualLocation(true);
              setLocationSearchQuery(place.formatted_address || place.name || '');
              
              if (mapInstanceRef.current) {
                mapInstanceRef.current.setCenter({ lat, lng });
                mapInstanceRef.current.setZoom(12);
                updateUserLocationMarker({ lat, lng });
              }
            }
          });

        } catch (error) {
          console.error('Error initializing autocomplete:', error);
        }
      }, 500);

      return () => {
        clearTimeout(timer);
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }
      };
    }
  }, [isMapLoaded, isOpen, mapInstanceRef.current]);

  // Fetch dispensaries when modal opens or location changes
  useEffect(() => {
    if (isOpen && product) {
      fetchProductDispensaries();
    }
  }, [isOpen, product, userLocation, searchRadius]);

  // Auto-center map on dispensaries when they load
  useEffect(() => {
    if (dispensaries.length > 0 && mapInstanceRef.current) {
      centerMapOnDispensaries();
    }
  }, [dispensaries]);

  // Handle dispensary selection from list
  useEffect(() => {
    if (selectedDispensary && mapInstanceRef.current) {
      highlightSelectedDispensary();
    }
  }, [selectedDispensary]);

  const fetchProductDispensaries = async () => {
    if (!product) return;
    
    setIsLoadingDispensaries(true);
    setError(null);
    
    try {
      let url = `${API_URL}/api/products/${product.id}/dispensaries`;
      const params = new URLSearchParams();
      
      if (userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
        params.append('radius', searchRadius);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dispensaries');
      }

      const data = await response.json();
      console.log('Dispensaries data:', data);
      
      setDispensaries(data.dispensaries || []);
      setPriceStats(data.price_stats || null);
      
      if (mapInstanceRef.current) {
        updateMapMarkers(data.dispensaries || []);
      }
      
    } catch (err) {
      setError(err.message);
      console.error('Error fetching dispensaries:', err);
    } finally {
      setIsLoadingDispensaries(false);
    }
  };

  // ENHANCED initializeMap with smart centering
  const initializeMap = () => {
    if (!window.google?.maps || !mapRef.current) {
      console.error('Google Maps API not loaded or map ref not available');
      return;
    }

    try {
      // Smart default center - use first dispensary location if available
      let defaultCenter = { lat: 37.7749, lng: -122.4194 }; // San Francisco fallback
      
      if (dispensaries.length > 0) {
        const firstDispensary = dispensaries[0];
        defaultCenter = {
          lat: parseFloat(firstDispensary.latitude),
          lng: parseFloat(firstDispensary.longitude)
        };
      } else if (userLocation) {
        defaultCenter = userLocation;
      }
      
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        zoom: 12,
        center: defaultCenter,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          {
            "featureType": "all",
            "elementType": "geometry.fill",
            "stylers": [{"color": "#1a1a1a"}]
          },
          {
            "featureType": "all", 
            "elementType": "labels.text.fill",
            "stylers": [{"color": "#e5e5e5"}]
          },
          {
            "featureType": "water",
            "elementType": "geometry.fill",
            "stylers": [{"color": "#0a0a0a"}]
          },
          {
            "featureType": "road",
            "elementType": "geometry.fill",
            "stylers": [{"color": "#333"}]
          }
        ]
      });

      console.log('Map initialized successfully with center:', defaultCenter);
      
      // If dispensaries are already loaded, center on them
      if (dispensaries.length > 0) {
        setTimeout(() => centerMapOnDispensaries(), 100);
      }
    } catch (error) {
      console.error('Error creating map instance:', error);
      setError('Failed to create map. Please refresh and try again.');
    }
  };

  // NEW: Smart map centering function
  const centerMapOnDispensaries = () => {
    if (!mapInstanceRef.current || dispensaries.length === 0) return;

    try {
      const bounds = new window.google.maps.LatLngBounds();
      
      // Add user location to bounds if available
      if (userLocation && showUserLocation) {
        bounds.extend(userLocation);
      }
      
      // Add all dispensary locations to bounds
      dispensaries.forEach(dispensary => {
        bounds.extend({
          lat: parseFloat(dispensary.latitude),
          lng: parseFloat(dispensary.longitude)
        });
      });

      // Fit map to bounds with padding
      mapInstanceRef.current.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });

      // If only one dispensary and no user location, ensure reasonable zoom
      if (dispensaries.length === 1 && (!userLocation || !showUserLocation)) {
        setTimeout(() => {
          if (mapInstanceRef.current.getZoom() > 15) {
            mapInstanceRef.current.setZoom(13);
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error centering map on dispensaries:', error);
    }
  };

  // NEW: Highlight selected dispensary on map
  const highlightSelectedDispensary = () => {
    if (!mapInstanceRef.current || !selectedDispensary) return;

    try {
      // Remove previous highlight
      if (selectedMarkerRef.current) {
        selectedMarkerRef.current.setMap(null);
        selectedMarkerRef.current = null;
      }

      // Create highlight marker for selected dispensary
      const position = {
        lat: parseFloat(selectedDispensary.latitude),
        lng: parseFloat(selectedDispensary.longitude)
      };

      selectedMarkerRef.current = new window.google.maps.Marker({
        position: position,
        map: mapInstanceRef.current,
        title: selectedDispensary.name,
        icon: dispensaryIconUrl ? {
        url: dispensaryIconUrl,
        scaledSize: new window.google.maps.Size(50, 50), // Larger than normal
        anchor: new window.google.maps.Point(25, 50)
        } : {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 15,
        fillColor: '#00d4aa',
        fillOpacity: 0.8,
        strokeColor: '#ffffff',
        strokeWeight: 3
        },
        zIndex: 1000 // Ensure it appears on top
      });

      // Center map on selected dispensary
      mapInstanceRef.current.setCenter(position);
      if (mapInstanceRef.current.getZoom() < 14) {
        mapInstanceRef.current.setZoom(14);
      }

      // Create info window for selected dispensary
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="color: #333; font-family: Arial, sans-serif;">
            <h3 style="margin: 0 0 8px 0; color: #00d4aa;">${selectedDispensary.name}</h3>
            <p style="margin: 0 0 4px 0; font-size: 12px;">${selectedDispensary.address}, ${selectedDispensary.city}, ${selectedDispensary.state}</p>
            ${selectedDispensary.product_info?.price ? 
              `<p style="margin: 0; font-weight: bold; color: #10b981;">$${selectedDispensary.product_info.price.toFixed(2)}</p>` : 
              ''
            }
            ${selectedDispensary.distance ? 
              `<p style="margin: 4px 0 0 0; font-size: 11px; color: #666;">${selectedDispensary.distance.toFixed(1)} miles away</p>` : 
              ''
            }
          </div>
        `
      });

      infoWindow.open(mapInstanceRef.current, selectedMarkerRef.current);

    } catch (error) {
      console.error('Error highlighting selected dispensary:', error);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return;
    }

    setIsLoadingLocation(true);
    setError(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        
        setUserLocation(location);
        setIsManualLocation(false);
        
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter(location);
          mapInstanceRef.current.setZoom(13);
          updateUserLocationMarker(location);
        }
        
        setIsLoadingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        setError('Unable to get your current location. Please enable location services or search for a location manually.');
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const updateUserLocationMarker = (location) => {
    if (!mapInstanceRef.current) return;

    if (userMarkerRef.current) {
      userMarkerRef.current.setMap(null);
    }
    
    userMarkerRef.current = new window.google.maps.Marker({
      position: location,
      map: mapInstanceRef.current,
      title: isManualLocation ? 'Selected Location' : 'Your Location',
      visible: showUserLocation,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: isManualLocation ? '#f59e0b' : '#00d4aa',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3
      }
    });
  };

  const updateMapMarkers = (dispensaryList) => {
    if (!mapInstanceRef.current) return;

    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    dispensaryList.forEach((dispensary, index) => {
      const marker = new window.google.maps.Marker({
        position: {
          lat: parseFloat(dispensary.latitude),
          lng: parseFloat(dispensary.longitude)
        },
        map: mapInstanceRef.current,
        title: dispensary.name,
        icon: dispensaryIconUrl ? {
          url: dispensaryIconUrl,
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 40)
        } : undefined
      });

      marker.addListener('click', () => {
        setSelectedDispensary(dispensary);
      });

      markersRef.current.push(marker);
    });

    // Auto-center if no user location is set
    if (!userLocation) {
      centerMapOnDispensaries();
    }
  };

  const handleClose = () => {
    // Clean up markers
    if (markersRef.current) {
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
    }
    if (userMarkerRef.current) {
      userMarkerRef.current.setMap(null);
      userMarkerRef.current = null;
    }
    if (selectedMarkerRef.current) {
      selectedMarkerRef.current.setMap(null);
      selectedMarkerRef.current = null;
    }
    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }
    if (mapInstanceRef.current) {
      mapInstanceRef.current = null;
    }
    
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    
    setUserLocation(null);
    setDispensaries([]);
    setSelectedDispensary(null);
    setError(null);
    setIsManualLocation(false);
    setLocationSearchQuery('');
    setShowUserLocation(true);
    setPriceStats(null);
    onClose();
  };

  const getSortedDispensaries = () => {
    if (!dispensaries.length) return [];

    const sorted = [...dispensaries].sort((a, b) => {
      switch (sortBy) {
        case 'price':
          const priceA = a.product_info?.price || Infinity;
          const priceB = b.product_info?.price || Infinity;
          return priceA - priceB;
        case 'distance':
          const distA = a.distance || Infinity;
          const distB = b.distance || Infinity;
          return distA - distB;
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    return sorted;
  };

  const formatPrice = (price) => {
    if (!price) return 'Price not available';
    return `$${price.toFixed(2)}`;
  };

  const PriceStatsCard = () => {
    if (!priceStats || priceStats.total_locations === 0) return null;

    return (
      <div style={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
        border: '1px solid #333',
        borderRadius: '12px',
        padding: isMobile ? '12px' : '16px',
        marginBottom: '16px'
      }}>
        <h4 style={{
          fontSize: isMobile ? '14px' : '16px',
          fontWeight: '600',
          color: '#e5e5e5',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <DollarSign size={isMobile ? 16 : 18} style={{ color: '#00d4aa' }} />
          Price Analysis
        </h4>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr', 
          gap: '12px',
          fontSize: isMobile ? '12px' : '14px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#10b981', fontWeight: '600', fontSize: isMobile ? '14px' : '16px' }}>
              {formatPrice(priceStats.min_price)}
            </div>
            <div style={{ color: '#888', fontSize: isMobile ? '10px' : '11px' }}>Lowest</div>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#ef4444', fontWeight: '600', fontSize: isMobile ? '14px' : '16px' }}>
              {formatPrice(priceStats.max_price)}
            </div>
            <div style={{ color: '#888', fontSize: isMobile ? '10px' : '11px' }}>Highest</div>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#8b5cf6', fontWeight: '600', fontSize: isMobile ? '14px' : '16px' }}>
              {formatPrice(priceStats.average_price)}
            </div>
            <div style={{ color: '#888', fontSize: isMobile ? '10px' : '11px' }}>Average</div>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#f59e0b', fontWeight: '600', fontSize: isMobile ? '14px' : '16px' }}>
              {priceStats.total_locations}
            </div>
            <div style={{ color: '#888', fontSize: isMobile ? '10px' : '11px' }}>Locations</div>
          </div>
        </div>
        
        {priceStats.median_price && (
          <div style={{
            marginTop: '12px',
            padding: '8px',
            background: colors.bgTertiary,
            borderRadius: '6px',
            textAlign: 'center',
            fontSize: isMobile ? '11px' : '12px',
            color: '#888'
          }}>
            Median Price: <span style={{ color: '#00d4aa', fontWeight: '600' }}>
              {formatPrice(priceStats.median_price)}
            </span>
          </div>
        )}
      </div>
    );
  };

  const DispensaryCard = ({ dispensary }) => (
    <div
      onClick={() => setSelectedDispensary(dispensary)}
      style={{
        padding: isMobile ? '12px' : '16px',
        background: selectedDispensary?.id === dispensary.id 
          ? 'linear-gradient(135deg, rgba(0, 212, 170, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)'
          : '#2a2a2a',
        border: selectedDispensary?.id === dispensary.id 
          ? '2px solid #00d4aa' 
          : '1px solid #333',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        marginBottom: '12px'
      }}
      onMouseEnter={(e) => {
        if (selectedDispensary?.id !== dispensary.id) {
          e.currentTarget.style.background = '#333';
        }
      }}
      onMouseLeave={(e) => {
        if (selectedDispensary?.id !== dispensary.id) {
          e.currentTarget.style.background = '#2a2a2a';
        }
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '8px'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: isMobile ? '14px' : '16px',
          fontWeight: '600',
          color: '#e5e5e5',
          flex: 1
        }}>
          {dispensary.name}
        </h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
          {dispensary.product_info?.price && (
            <div style={{
              background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
              color: '#0a0a0a',
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: isMobile ? '12px' : '13px',
              fontWeight: '700'
            }}>
              {formatPrice(dispensary.product_info.price)}
            </div>
          )}
          
          {dispensary.distance && (
            <div style={{
              background: '#333',
              color: '#e5e5e5',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: isMobile ? '10px' : '11px',
              fontWeight: '500'
            }}>
              {dispensary.distance.toFixed(1)} mi
            </div>
          )}
        </div>
      </div>

      <p style={{
        margin: '0 0 8px 0',
        fontSize: isMobile ? '11px' : '12px',
        color: '#888',
        lineHeight: '1.4'
      }}>
        {dispensary.address && `${dispensary.address}, `}
        {dispensary.city}, {dispensary.state}
      </p>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {dispensary.product_info?.variant && (
            <div style={{
              background: colors.bgTertiary,
              color: '#888',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: isMobile ? '9px' : '10px'
            }}>
              {dispensary.product_info.variant}
            </div>
          )}
          
          {dispensary.product_info?.platform_rating && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <Star size={10} style={{ color: '#fbbf24', fill: '#fbbf24' }} />
              <span style={{ fontSize: isMobile ? '10px' : '11px', color: '#e5e5e5' }}>
                {dispensary.product_info.platform_rating}
              </span>
            </div>
          )}
        </div>

        {dispensary.product_info?.url && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              window.open(dispensary.product_info.url, '_blank');
            }}
            style={{
              padding: '4px 8px',
              background: '#00d4aa',
              color: '#0a0a0a',
              border: 'none',
              borderRadius: '4px',
              fontSize: isMobile ? '9px' : '10px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <ExternalLink size={8} />
            View
          </button>
        )}
      </div>
    </div>
  );

  if (!isOpen) return null;

  // Mobile Layout
  if (isMobile) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Mobile Header */}
        <div style={{
          padding: '16px',
          background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '700',
              color: '#e5e5e5',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Store style={{ color: '#00d4aa' }} size={18} />
              {product?.name}
            </h2>
            <p style={{
              margin: '2px 0 0 26px',
              color: '#888',
              fontSize: '11px'
            }}>{dispensaries.length} locations found
            </p>
          </div>
          
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Location Controls for Mobile */}
        <div style={{
          padding: '12px',
          background: colors.bgCard,
          borderBottom: '1px solid #333'
        }}>
          <div style={{ marginBottom: '8px' }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search location for nearby dispensaries..."
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '6px',
                color: '#e5e5e5',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={getCurrentLocation}
              disabled={isLoadingLocation}
              style={{
                flex: 1,
                padding: '8px 12px',
                background: isLoadingLocation ? '#333' : 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                color: isLoadingLocation ? '#888' : '#0a0a0a',
                border: 'none',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: isLoadingLocation ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px'
              }}
            >
              {isLoadingLocation ? (
                <Loader size={10} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Target size={10} />
              )}
              Use GPS
            </button>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '8px',
                background: colors.bgTertiary,
                border: '1px solid #333',
                borderRadius: '6px',
                color: '#e5e5e5',
                fontSize: '11px',
                cursor: 'pointer'
              }}
            >
              <option value="price">Sort by Price</option>
              <option value="distance">Sort by Distance</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>

          {userLocation && (
            <div style={{ marginTop: '8px' }}>
              <label style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '11px',
                color: '#888'
              }}>
                Search Radius: {searchRadius} miles
              </label>
              <input
                type="range"
                min="5"
                max="50"
                value={searchRadius}
                onChange={(e) => setSearchRadius(Number(e.target.value))}
                style={{
                  width: '100%',
                  accentColor: '#00d4aa'
                }}
              />
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(239, 68, 68, 0.1)',
            borderBottom: `1px solid ${colors.border}`,
            color: '#ef4444',
            fontSize: '12px'
          }}>
            {error}
          </div>
        )}

        {/* Mobile Content */}
        <div style={{ flex: 1, overflow: 'hidden', background: colors.bgTertiary, }}>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '12px' }}>
            
            <PriceStatsCard />

            {/* Loading State */}
            {isLoadingDispensaries && (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#888',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'}}>
                <Loader size={20} style={{ animation: 'spin 1s linear infinite', marginBottom: '8px', color: '#00d4aa' }} />
                <p style={{ margin: 0, fontSize: '12px' }}>Finding dispensaries...</p>
              </div>
            )}

            {/* Dispensary List */}
            {!isLoadingDispensaries && (
              <div style={{ 
                flex: 1, 
                overflowY: 'auto'
              }}>
                {dispensaries.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#888'
                  }}>
                    <Store size={32} style={{ marginBottom: '12px' }} />
                    <p style={{ margin: 0, fontSize: '14px', marginBottom: '8px' }}>
                      No dispensaries found carrying this product
                    </p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                      Try expanding your search radius or check back later
                    </p>
                  </div>
                ) : (
                  getSortedDispensaries().map((dispensary) => (
                    <DispensaryCard key={dispensary.id} dispensary={dispensary} />
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Desktop Layout
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '20px',
        width: '100%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: `1px solid ${colors.border}`,
          background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: '700',
              color: '#e5e5e5',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Store style={{ color: '#00d4aa' }} size={24} />
              Find {product?.name}
            </h2>
            <p style={{
              margin: '4px 0 0 36px',
              color: '#888',
              fontSize: '14px'
            }}>
              {dispensaries.length > 0 
                ? `Found at ${dispensaries.length} dispensaries${userLocation ? ` within ${searchRadius} miles` : ''}`
                : 'Searching for dispensaries carrying this product...'
              }
            </p>
          </div>
          
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#888';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            padding: '16px 24px',
            background: 'rgba(239, 68, 68, 0.1)',
            borderBottom: `1px solid ${colors.border}`,
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <X size={16} />
            {error}
          </div>
        )}

        {/* Content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '400px 1fr',
          height: 'calc(90vh - 140px)',
          overflow: 'hidden'
        }}>
          {/* Left Panel */}
          <div style={{
            borderRight: '1px solid #333',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0
          }}>
            {/* Location Controls */}
            <div style={{
              padding: '16px',
              borderBottom: `1px solid ${colors.border}`,
              background: colors.bgCard,
              flexShrink: 0
            }}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontWeight: '500',
                  color: '#e5e5e5',
                  fontSize: '14px'
                }}>
                  Search Location (Optional)
                </label>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search for your location..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                <button
                  onClick={getCurrentLocation}
                  disabled={isLoadingLocation}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    background: isLoadingLocation ? '#333' : 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                    color: isLoadingLocation ? '#888' : '#0a0a0a',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: isLoadingLocation ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  {isLoadingLocation ? (
                    <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <Target size={14} />
                  )}
                  Use GPS Location
                </button>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{
                    padding: '10px',
                    background: colors.bgTertiary,
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="price">Price</option>
                  <option value="distance">Distance</option>
                  <option value="name">Name</option>
                </select>
              </div>

              {userLocation && (
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: '#e5e5e5',
                    fontSize: '14px'
                  }}>
                    Search Radius: {searchRadius} miles
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="50"
                    value={searchRadius}
                    onChange={(e) => setSearchRadius(Number(e.target.value))}
                    style={{
                      width: '100%',
                      accentColor: '#00d4aa'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Price Stats */}
            <div style={{ flexShrink: 0, padding: '16px', borderBottom: '1px solid #333' }}>
              <PriceStatsCard />
            </div>

            {/* Loading State */}
            {isLoadingDispensaries && (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#888',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'
              }}>
                <Loader size={24} style={{
                  animation: 'spin 1s linear infinite',
                  marginBottom: '12px',
                  color: '#00d4aa'
                }} />
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Finding dispensaries...
                </p>
              </div>
            )}

            {/* Dispensary List */}
            {!isLoadingDispensaries && (
              <div style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                padding: '16px'
              }}>
                {dispensaries.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#888'
                  }}>
                    <Store size={48} style={{ marginBottom: '16px' }} />
                    <h3 style={{ margin: '0 0 8px 0', color: '#e5e5e5' }}>
                      No dispensaries found
                    </h3>
                    <p style={{ margin: 0, fontSize: '14px' }}>
                      This product isn't currently available at dispensaries in our database.
                    </p>
                    {userLocation && (
                      <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                        Try expanding your search radius or check back later.
                      </p>
                    )}
                  </div>
                ) : (
                  getSortedDispensaries().map((dispensary) => (
                    <DispensaryCard key={dispensary.id} dispensary={dispensary} />
                  ))
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Map */}
          <div style={{ position: 'relative', background: '#2a2a2a' }}>
            {!isMapLoaded ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#888'
              }}>
                <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ marginLeft: '12px' }}>Loading map...</span>
              </div>
            ) : (
              <>
                <div
                  ref={mapRef}
                  style={{
                    width: '100%',
                    height: '100%'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  {showUserLocation && userLocation && (
                    <div style={{ 
                      color: isManualLocation ? '#f59e0b' : '#00d4aa', 
                      marginBottom: '4px' 
                    }}>
                      ● {isManualLocation ? 'Custom Location' : 'Your Location'}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Store size={14} />
                    {dispensaries.length} Dispensaries
                  </div>
                  {selectedDispensary && (
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '4px 0',
                      borderTop: '1px solid #333',
                      color: '#00d4aa',
                      fontSize: '11px'
                    }}>
                      Selected: {selectedDispensary.name}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .pac-container {
          display: block !important;
          background-color: #2a2a2a !important;
          border: 1px solid #333 !important;
          border-radius: 8px !important;
          z-index: 100001 !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5) !important;
        }
        
        .pac-item {
          background-color: #2a2a2a !important;
          color: #e5e5e5 !important;
          border-top: 1px solid #333 !important;
          padding: 8px 12px !important;
          cursor: pointer !important;
          font-size: 14px !important;
        }
        
        .pac-item:hover {
          background-color: #00d4aa !important;
          color: #0a0a0a !important;
        }
        
        .pac-item:first-child {
          border-top: none !important;
        }
        
        .pac-item-query {
          color: #e5e5e5 !important;
          font-size: 14px !important;
        }
        
        .pac-matched {
          font-weight: bold !important;
          color: #00d4aa !important;
        }
      `}</style>
    </div>
  );
};